import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { useInView } from '@/hooks/useInView';
import { imgSrc } from '@/lib/img';

/**
 * TwitterCardsRow — Brand-spec card layout.
 *
 * Structure (per card):
 *   ┌──────────────────────────────────────┐
 *   │  [colored card · padded interior]    │
 *   │   ┌──────────────────────────────┐   │
 *   │   │  [smaller photo container]   │   │
 *   │   │                              │   │
 *   │   │                       [LOGO] │   │  ← logo overlaps ON TOP of photo
 *   │   └──────────────────────────────┘   │
 *   │                                      │
 *   │   [Pack of N pill]                   │  ← pill is in content section
 *   │                                      │
 *   │   3D Choco Pops                      │  ← title
 *   │                                      │
 *   │   ✓ Perk 1                           │
 *   │   ✓ Perk 2                           │
 *   │   ✓ Perk 3                           │
 *   ├──────────────────────────────────────┤
 *   │   ₹1,189       Build your pack →     │  ← white footer bar
 *   └──────────────────────────────────────┘
 *
 * Two variants:
 *   - shop: body is a short subtitle string, CTA "Go to Shop"
 *   - customise: body is an array of perks rendered with CheckCircle2, CTA "Build your pack"
 */

const TONE_CONFIG = {
  cyan: {
    bgClass: 'bg-[var(--bb-cyan)]',
    titleClass: 'text-white',
    bodyClass: 'text-white',
    perkIconClass: 'text-white',
    ctaClass: 'text-white',
    packPillBg: 'bg-white',
    packPillText: 'text-[var(--bb-darkblue)]',
  },
  pink: {
    bgClass: 'bg-[var(--bb-pink)]',
    titleClass: 'text-white',
    bodyClass: 'text-white',
    perkIconClass: 'text-white',
    ctaClass: 'text-white',
    packPillBg: 'bg-white',
    packPillText: 'text-[var(--bb-darkblue)]',
  },
  yellow: {
    bgClass: 'bg-[var(--bb-yellow)]',
    titleClass: 'text-[var(--bb-darkblue)]',
    bodyClass: 'text-[var(--bb-darkblue)]',
    perkIconClass: 'text-[var(--bb-darkblue)]',
    ctaClass: 'text-[var(--bb-darkblue)]',
    packPillBg: 'bg-[var(--bb-darkblue)]',
    packPillText: 'text-white',
  },
};

const TwitterCard = ({ card, ctaLabel }) => {
  const tone = TONE_CONFIG[card.tone] || TONE_CONFIG.cyan;
  const isPerks = Array.isArray(card.body);
  const isComingSoon = !!card.comingSoon;
  const resolvedCtaLabel = card.ctaLabelOverride || ctaLabel;

  return (
    <Link
      to={card.to}
      data-testid={card.testid}
      aria-label={`${card.title} — ${resolvedCtaLabel}`}
      className={`group relative rounded-[28px] overflow-hidden ${tone.bgClass} flex flex-col bb-press-pop focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--bb-darkblue)]`}
    >
      {/* COMING SOON ribbon — top-left */}
      {isComingSoon && (
        <div
          className="absolute top-0 left-0 z-20 pointer-events-none"
          data-testid={`${card.testid}-coming-soon-ribbon`}
        >
          <div className="bg-[var(--bb-darkblue)] text-white text-[10px] tracking-[0.14em] font-nunito-black uppercase px-3 py-1.5 rounded-br-2xl shadow-md">
            Coming Soon
          </div>
        </div>
      )}
      {/* Padded interior so the colored card shows around the photo */}
      <div className="p-3 sm:p-3.5">
        <div className="relative">
          {/* Smaller photo container — inner, with rounded corners */}
          <div className={`rounded-2xl overflow-hidden bg-white aspect-[4/3] ${isComingSoon ? 'opacity-70 saturate-[0.8]' : ''}`}>
            <img
              src={imgSrc(card.photo, 'card')}
              alt={card.title}
              className="w-full h-full object-cover bb-hover-zoom-target"
              loading="lazy"
              decoding="async"
            />
          </div>
          {/* Brand logo straddles the photo's bottom-right edge:
              half sits on the photo, half overlaps into the colored content area below */}
          {card.logo && (
            <img
              src={card.logo}
              alt=""
              className="absolute -bottom-10 right-2 sm:right-3 w-24 h-24 md:w-28 md:h-28 object-contain z-10 drop-shadow-[0_4px_10px_rgba(0,0,0,0.22)] select-none pointer-events-none bb-hover-wobble-target"
              data-testid={`${card.testid}-logo`}
            />
          )}
        </div>
      </div>

      {/* Content section (in the colored area). The logo overlaps the top-right
          of this area; the pill below sits on the left so they share the same row
          without colliding. Title & perks flow below the logo's bottom edge. */}
      <div className="px-5 pb-4 flex-1 flex flex-col">
        {/* Pack of N pill — left aligned, sits opposite the overlapping logo */}
        <span
          className={`self-start inline-flex items-center px-3 py-1 rounded-full text-[11px] font-nunito-black uppercase tracking-wider ${tone.packPillBg} ${tone.packPillText}`}
          data-testid={`${card.testid}-pack-pill`}
        >
          Pack of {card.packSize}
        </span>

        <h3 className={`font-display text-2xl md:text-[28px] leading-[0.95] mt-3 ${tone.titleClass}`}>
          {card.title}
        </h3>

        {isPerks ? (
          <ul className={`mt-3 space-y-1.5 text-[13px] md:text-sm font-nunito-medium ${tone.bodyClass}`}>
            {card.body.map((perk) => (
              <li key={perk} className="flex items-start gap-1.5">
                <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${tone.perkIconClass}`} />
                <span>{perk}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={`mt-2 text-sm font-nunito-medium opacity-95 ${tone.bodyClass}`}>{card.body}</p>
        )}
      </div>

      {/* CTA sits directly on the card colour — centered */}
      <div className="px-5 pb-5 pt-2 flex items-center justify-center">
        <div className={`inline-flex items-center gap-1.5 font-nunito-black text-sm ${tone.ctaClass}`}>
          {resolvedCtaLabel}
          <ArrowRight className="w-4 h-4 bb-hover-arrow-target" />
        </div>
      </div>
    </Link>
  );
};

export const TwitterCardsRow = ({ cards, ctaLabel = 'Go to Shop', className = '' }) => {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      className={`grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 ${className}`}
      data-testid="twitter-cards-row"
    >
      {cards.slice(0, 3).map((c, idx) => (
        <div
          key={c.testid + c.to}
          className={inView ? 'bb-anim-scale-in' : 'opacity-0'}
          style={{ animationDelay: inView ? `${idx * 130}ms` : undefined }}
        >
          <TwitterCard card={c} ctaLabel={ctaLabel} />
        </div>
      ))}
    </div>
  );
};

export default TwitterCardsRow;
